import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as opsApi from "@/features/ops/api";
import type { TranscriptDetail } from "@/features/ops/api";
import { formatCents } from "@/lib/utils";

/** Parsed transcript detail (Code Lab drilldown target) — header + the full
 * transaction-code list for one tax record. */
export default function TranscriptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TranscriptDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    opsApi
      .getTranscriptDetail(id)
      .then(setData)
      .catch((err) => {
        setNotFound(true);
        toast.error(err instanceof Error ? err.message : "Could not load the transcript.");
      });
  }, [id]);

  if (notFound) {
    return <p className="text-muted-foreground text-sm">Transcript not found.</p>;
  }
  if (!data) {
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/admin/ops/codes" className="text-primary text-sm font-medium hover:underline">
        ← Back to Code Lab
      </Link>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {data.client} — {data.taxYear} Form {data.form}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">Firm: </span>
            {data.firm}
          </div>
          <div>
            <span className="text-muted-foreground">Taxpayer/Spouse: </span>
            {data.forWho === "S" ? "Spouse" : "Taxpayer"}
          </div>
          <div>
            <span className="text-muted-foreground">Balance: </span>
            {formatCents(data.balanceCents)}
          </div>
          <div>
            <span className="text-muted-foreground">Total owed: </span>
            {formatCents(data.totalOwedCents)}
          </div>
          <div>
            <span className="text-muted-foreground">CSED: </span>
            {data.csedDate ?? "—"}
          </div>
        </CardContent>
      </Card>

      <Card className="gap-0 overflow-hidden p-0">
        <div className="flex items-center gap-2 px-4 pt-4">
          <h2 className="text-foreground font-semibold">Transactions</h2>
          <Badge variant="secondary">{data.transactions.length}</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Code</TableHead>
              <TableHead>Explanation</TableHead>
              <TableHead className="w-32">Date</TableHead>
              <TableHead className="w-32 text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground text-center text-sm">
                  No transactions on this transcript.
                </TableCell>
              </TableRow>
            ) : (
              data.transactions.map((t, i) => (
                <TableRow key={`${t.code}-${i}`}>
                  <TableCell className="font-medium">{t.code}</TableCell>
                  <TableCell>{t.explanation || "—"}</TableCell>
                  <TableCell>{t.date ?? "—"}</TableCell>
                  <TableCell className="text-right">{formatCents(t.amountCents)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
